/**
 * Notification Popup Component
 *
 * Displays notifications grouped by calendar week with pagination.
 * Week grouping:
 * - Current week: "This week"
 * - Past weeks: "Week Jan 5 - Jan 11" (Sunday to Saturday)
 *
 * Features:
 * - Pagination (20 items per page, backend-controlled)
 * - Click notification → navigate to landing_url
 * - Relative timestamps ("2 hours ago")
 */

import React, { useEffect, useState, useMemo } from 'react';
import { toSafeISOString } from '@/utils/date';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AsyncButton, Button } from '@/components/ui';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: GET_NOTIFICATIONS is a silent paginated fetch; MARK_NOTIFICATION_READ is fire-and-forget optimistic
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import type {
  GetNotificationsInput,
  GetNotificationsOutput,
  Notification,
} from '@/types/notification.types';
import type { MarkNotificationReadInput } from '@/types/generated';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { sizing } from '@/styles/design-tokens';
import { logger } from '@/utils/logger';
import { useModalManager } from '@/hooks/useModalManager';
import { ModalId } from '@/types/modal-manager.types';
import { parseDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { ShareButton } from '@/components/core/ShareButton';
import {
  startOfWeek,
  endOfWeek,
  isSameWeek,
  format,
  formatDistanceToNow,
  parseISO,
} from 'date-fns';

interface NotificationPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GroupedNotifications {
  weekLabel: string;
  weekStart: Date;
  notifications: Notification[];
}

export default function NotificationPopup({
  isOpen,
  onClose,
}: NotificationPopupProps) {
  const colors = useThemeColors();
  const { openModal, closeModal } = useModalManager();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track modal open/close state
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.NOTIFICATIONS);
    } else {
      closeModal(ModalId.NOTIFICATIONS);
    }
  }, [isOpen, openModal, closeModal]);

  // Fetch notifications when popup opens
  useEffect(() => {
    if (isOpen) {
      // Reset state and fetch first page
      setNotifications([]);
      setCurrentPage(1);
      setHasMore(false);
      fetchNotifications(1, true);
    }
  }, [isOpen]);

  const fetchNotifications = async (page: number, replace: boolean = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await actionOrchestrator.execute<
        GetNotificationsInput,
        GetNotificationsOutput
      >(Action.GET_NOTIFICATIONS, {
        page,
      });

      if (result.code === ResponseCode.SUCCESS && result.data) {
        if (replace) {
          setNotifications(result.data.notifications);
        } else {
          // Append to existing notifications
          setNotifications(prev => [
            ...prev,
            ...(result.data?.notifications || []),
          ]);
        }
        setHasMore(result.data?.has_more || false);
      } else {
        setError('Failed to load notifications. Please try again.');
        logger.error('Failed to fetch notifications', { code: result.code });
      }
    } catch (err) {
      setError('Failed to load notifications. Please try again.');
      logger.error('Error fetching notifications', { error: err });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchNotifications(nextPage, false);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.landing_url) return;

    // Optimistically mark as read
    if (!notification.read_at) {
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id
            ? { ...n, read_at: toSafeISOString(new Date()) }
            : n
        )
      );
      actionOrchestrator
        .execute<
          MarkNotificationReadInput,
          { notification_id: string }
        >(Action.MARK_NOTIFICATION_READ, { notification_id: notification.id })
        .catch(() => {
          // silent fail — optimistic update stays
        });
    }

    window.open(notification.landing_url, '_blank');
    onClose();
  };

  const handleMarkAsRead = async (
    notificationId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    try {
      const result = await actionOrchestrator.execute<
        MarkNotificationReadInput,
        { notification_id: string }
      >(Action.MARK_NOTIFICATION_READ, { notification_id: notificationId });

      if (result.code === ResponseCode.SUCCESS) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId
              ? { ...n, read_at: toSafeISOString(new Date()) }
              : n
          )
        );
      }
    } catch {
      // silent fail for mark-as-read
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Group notifications by calendar week
  const groupedNotifications = useMemo(() => {
    if (notifications.length === 0) return [];

    const groups = new Map<string, GroupedNotifications>();
    const now = new Date();

    for (const notification of notifications) {
      const date = parseISO(notification.created_at);
      const weekStart = startOfWeek(date, { weekStartsOn: 0 }); // Sunday
      const weekEnd = endOfWeek(date, { weekStartsOn: 0 }); // Saturday

      const isCurrentWeek = isSameWeek(date, now, { weekStartsOn: 0 });

      // Create week label
      const weekLabel = isCurrentWeek
        ? 'This week'
        : `Week ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;

      // Create week key for grouping (use weekStart timestamp)
      const weekKey = weekStart.getTime().toString();

      if (!groups.has(weekKey)) {
        groups.set(weekKey, {
          weekLabel,
          weekStart,
          notifications: [],
        });
      }

      groups.get(weekKey)!.notifications.push(notification);
    }

    // Convert to array and sort by week (newest first)
    return Array.from(groups.values()).sort(
      (a, b) => b.weekStart.getTime() - a.weekStart.getTime()
    );
  }, [notifications]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl flex flex-col"
        style={{
          height: 'min(calc(600px * var(--sizing-multiplier, 1)), 90vh)',
        }}
        onKeyDown={handleKeyDown}
      >
        <div className="flex-1 overflow-y-auto px-2 -mx-2 min-h-0">
          {isLoading && notifications.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div
                className="animate-spin rounded-full border-4 border-t-transparent"
                style={{
                  width: sizing.icon.lg,
                  height: sizing.icon.lg,
                  borderColor: colors.border.default,
                  borderTopColor: 'transparent',
                }}
              />
              <span className="ml-3" style={{ color: colors.text.secondary }}>
                Loading notifications...
              </span>
            </div>
          )}

          {error && (
            <div
              className="px-4 py-3 rounded-lg mb-4 border"
              style={{
                backgroundColor: colors.error + '20',
                borderColor: colors.error + '40',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name={IconName.ALERT_CIRCLE}
                  size="sm"
                  style={{ color: colors.error }}
                />
                <span className="font-semibold" style={{ color: colors.error }}>
                  Error
                </span>
              </div>
              <p className="text-sm" style={{ color: colors.error }}>
                {error}
              </p>
              <Button
                onClick={() => fetchNotifications(1, true)}
                size="sm"
                className="mt-3"
              >
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !error && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon
                name={IconName.BELL}
                size="xl"
                style={{ color: colors.text.secondary }}
                className="mb-4"
              />
              <p
                className="text-lg font-medium"
                style={{ color: colors.text.secondary }}
              >
                No notifications yet
              </p>
              <p
                className="text-sm mt-2"
                style={{ color: colors.text.tertiary }}
              >
                You'll see notifications here when there's something new
              </p>
            </div>
          )}

          {!error && notifications.length > 0 && (
            <div className="space-y-6">
              {groupedNotifications.map((group, groupIndex) => (
                <div key={groupIndex}>
                  {/* Week Header */}
                  <div
                    className="px-3 py-2 font-semibold text-sm sticky top-0 z-10"
                    style={{
                      backgroundColor: colors.background,
                      color: colors.text.secondary,
                    }}
                  >
                    {group.weekLabel}
                  </div>

                  {/* Notifications in this week */}
                  <div className="space-y-1">
                    {group.notifications.map(notification => {
                      // Parse landing URL to extract entity and ID for ShareButton
                      const deepLink = notification.landing_url
                        ? parseDeepLink(notification.landing_url)
                        : null;
                      const isRead = !!notification.read_at;

                      return (
                        <button
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="group relative w-full px-4 py-3 text-left rounded-lg transition-colors"
                          style={{
                            backgroundColor: 'transparent',
                            color: colors.text.primary,
                            opacity: isRead ? 0.5 : 1,
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor =
                              colors.hover.background;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          }}
                        >
                          {/* Hover-reveal buttons — only render when there's something to show */}
                          {(!isRead || deepLink) && (
                            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                              {!isRead && (
                                <button
                                  onClick={e =>
                                    handleMarkAsRead(notification.id, e)
                                  }
                                  className="p-1 rounded transition-colors"
                                  style={{}}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      colors.hover.background;
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      'transparent';
                                  }}
                                  title="Mark as read"
                                >
                                  <Icon
                                    name={IconName.CHECK}
                                    size="sm"
                                    style={{ color: colors.text.tertiary }}
                                  />
                                </button>
                              )}
                              {deepLink && (
                                <ShareButton
                                  entity={deepLink.entity as DeepLinkEntity}
                                  id={deepLink.id}
                                  ariaLabel="Copy notification link"
                                />
                              )}
                            </div>
                          )}

                          <div className="font-medium text-base mb-1">
                            {notification.title}
                          </div>
                          {notification.message && (
                            <div
                              className="text-sm mb-2"
                              style={{ color: colors.text.secondary }}
                            >
                              {notification.message}
                            </div>
                          )}
                          <div
                            className="text-xs"
                            style={{ color: colors.text.tertiary }}
                          >
                            {formatDistanceToNow(
                              parseISO(notification.created_at),
                              {
                                addSuffix: true,
                              }
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {hasMore && (
                <div className="px-3 py-4">
                  <AsyncButton
                    onClick={handleLoadMore}
                    isLoading={isLoading}
                    variant="secondary"
                    className="w-full"
                    icon={IconName.CHEVRON_DOWN}
                  >
                    Load More
                  </AsyncButton>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
